/**
 * Translate an AppsFlyer event into the GA4 event GA4 and Google Ads actually understand.
 *
 * Every AppsFlyer event on this surface is mirrored into GA4 (`logEvent` in `appsflyer.ts`), and it
 * used to be mirrored VERBATIM — same name, same params. That reads fine and is silently useless for
 * the one thing GA4 is there for:
 *
 * | funnel step | what GA4 received | what GA4 needs |
 * |---|---|---|
 * | package picker | `af_content_view` | `view_item_list` |
 * | purchase page | `af_initiated_checkout` | `begin_checkout` |
 * | premium purchase | `af_purchase` + `af_revenue`/`af_currency` | not mirrored — see below |
 *
 * GA4 recognises a fixed set of names as commerce events and reads revenue from `value` + `currency`
 * and nothing else. `af_purchase` is a custom event to GA4 and `af_revenue` is a custom param, so the
 * property has been receiving purchases it cannot count — **no revenue at all, and nothing an Ads
 * campaign can optimise toward**, which is exactly the gap the App-campaigns request names.
 *
 * ## Why a mapper rather than changing what we send AppsFlyer
 *
 * The two destinations have genuinely different vocabularies and both are right on their own side.
 * AppsFlyer's PBA pipeline keys off `eventRevenue`/`eventRevenueCurrency`, the mobile SDK convention
 * is `af_revenue`/`af_currency`, and native mirrors those names 1:1 — changing them to please GA4
 * would break AppsFlyer and the native parity in one go. So the AppsFlyer call stays exactly as it
 * is, and this is the one place that says what GA4 should hear instead.
 *
 * ## Why REPLACE rather than send both
 *
 * Sending `purchase` alongside `af_purchase` would put two rows in GA4 for one payment. `af_purchase`
 * is not a recognised commerce event, so nothing in GA4 is counting it as revenue today and nothing
 * downstream can be relying on it as one; a duplicate that IS counted is the worse outcome — inflated
 * revenue is harder to notice, and harder to unpick, than absent revenue.
 *
 * ## Why `af_purchase` is NOT mirrored at all
 *
 * GA4 hears about a purchase exactly once per account: `symphony_premium`, for the FIRST paid
 * subscription, sent server-side by the harness from agent-payments' `SUBSCRIPTION_INITIAL` purchase
 * (`orion-harness-poc` `credits/ga4-premium-report.ts`). Never a top-up, a renewal or an upgrade, and
 * never from a client (decided 2026-10-11). AppsFlyer still gets `af_purchase` for every purchase.
 *
 * Anything without a GA4 equivalent passes through unchanged: a custom event is a custom event on
 * both sides, and inventing GA4 names for `symphony_active_user` and friends would be churn with no
 * reader.
 */

export interface Ga4MirroredEvent {
  name: string;
  params: Record<string, unknown>;
}

/** Values GA4 accepts. A boolean is dropped by the wire, so anything non-primitive is left out. */
const isWireValue = (value: unknown): boolean => typeof value === 'string' || typeof value === 'number';

/**
 * The GA4 event to send for an AppsFlyer one.
 *
 * Pure, and exported so the mapping is testable without a browser, an SDK or a network — the mapping
 * IS the contract with whoever reads the GA4 reports, and it is the part that silently stops being
 * true if somebody renames an AppsFlyer event without looking here.
 */
export function ga4EventFor(afName: string, afValue: Record<string, unknown> = {}): Ga4MirroredEvent | null {
  switch (afName) {
    // The picker is a LIST of plans, which is `view_item_list` in GA4's commerce vocabulary. The
    // AppsFlyer `af_content_type` becomes GA4's `item_list_name` — the same fact under the name the
    // reports group by.
    case 'af_content_view': {
      const params: Record<string, unknown> = { item_list_name: afValue.af_content_type ?? 'credits_packages' };
      if (isWireValue(afValue.origin)) {
        params.origin = afValue.origin;
      }
      return { name: 'view_item_list', params };
    }

    // Reaching the purchase page IS the start of checkout.
    case 'af_initiated_checkout':
      return { name: 'begin_checkout', params: {} };

    // Not mirrored: GA4 hears about purchases only as the server's `symphony_premium`.
    case 'af_purchase':
      return null;

    default:
      return { name: afName, params: afValue };
  }
}
